import { all, call, delay, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  actionRequest,
  completeRequest,
  dialogueRequest,
  moduleRequest,
  verificationRequest,
} from 'core/services/product';
import { selectRegister } from 'core/selectors/product/register';
import { IRegisterReducer, next } from 'core/slices/register';
import { IPlayerResponse, IResponseQueueItem } from 'types/action/player';
import { loadModule } from '../module/load';
import { IToken } from 'core/slices/session';
import { selectToken } from 'core/selectors/product/token';
import { IModuleAssign, ModuleType } from 'types/domain/module';
import { ICourseNodeDAO } from 'types/domain/course';
import { updatePageIds, waitForPageTransition } from './page';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { startModule } from '../module/start';
import { queueItem } from 'core/slices/queue';
import { updateState } from 'core/actions/product/state';
import { VISUAL_FEEDBACK_DELAY } from 'constants/index';

// The answer saga (module/action.ts `handleRegister`) holds an answered action on
// screen for its feedback window before advancing. Anything that fires off the same
// `registerActionResult` must outlast that window before deciding the player is stuck,
// or it advances a second time for one answer. Sized against the *longest* feedback
// delay (actions with `visualFeedback` on), plus room for the branch/out work that can
// follow it — derived so the two can't drift apart.
const RESULT_ADVANCE_GRACE = VISUAL_FEEDBACK_DELAY + 800;

export function* handlePlayerResponse(playerResponse: IPlayerResponse, meta?: { arg?: { result?: number | null } }) {
  const { queue, courseProgressId } = playerResponse;

  if (!queue.length) {
    // Race condition: the server queue can come back empty (e.g. from an actionRequest
    // or completeRequest) while the locally-derived action feed already holds future
    // actions computed from the module flow graph. Returning early here would skip the
    // `register.isNext -> next()` advance below and wedge the player on a loading spinner
    // even though there is somewhere to go. If future actions exist, proceed as normal.
    const feed: IActionFeed = yield select(selectActionFeed);
    const register: IRegisterReducer = yield select(selectRegister);

    if (!register.isNext || feed.feed.length <= feed.index + 1) return;

    // ...but when this response is the round-trip of an *answered* action, the answer
    // saga already owns that action's advance: it shows feedback, then advances itself
    // (honouring PASS/FAIL outs). The request round-trip usually beats that window, so
    // advancing here as well moves the learner two slides for one answer — the module
    // then ends short of its action count and is scored as a fail. This branch is only
    // a wedge-guard, so let the owner have its window and step in afterwards, and only
    // if nothing moved. Observing the outcome rather than enumerating the owner's cases
    // keeps the guard intact for results it doesn't advance on.
    if (meta?.arg?.result != null) {
      const index = register.index;
      yield delay(RESULT_ADVANCE_GRACE);

      const settled: IRegisterReducer = yield select(selectRegister);

      if (!settled.isNext || settled.index !== index) return;
    }

    yield put(yield call(next, { culprit: 'handlePlayerResponse:emptyQueue' }));

    return;
  }

  yield waitForPageTransition();

  const token: IToken = yield select(selectToken);

  yield all(
    queue.map(item =>
      processQueueItem(item, courseProgressId || token.courseProgressId, token.token || playerResponse.token),
    ),
  );

  const actionId = yield select((state: RootState) => state.state[EntityTypes.ACTION]);
  if (!actionId) yield updatePageIds();

  const register: IRegisterReducer = yield select(selectRegister);
  if (register.isNext) yield put(yield call(next, { culprit: 'handlePlayerResponse' }));
}

function* processQueueItem(item: IResponseQueueItem, courseProgressId: number, token: string) {
  if (item.module) yield prepareModule(item.module, courseProgressId, item.courseNodeId);
  else if (item.courseNode) yield registerDialogue(item.courseNode, courseProgressId, token);
  else if (item.courseNodeMenu) yield registerDialogue(item.courseNodeMenu, courseProgressId, token);
}

function* registerDialogue(cn: ICourseNodeDAO, courseProgressId: number, token: string) {
  yield put(yield call(queueItem, { courseNodeId: cn.id, courseProgressId, courseNodeType: cn.type }));
  const feed: IActionFeed = yield select(selectActionFeed);
  const courseNodeId = yield select(state => state.state[EntityTypes.COURSE_NODE]);
  const current = courseNodeId
    ? feed.feed.find(item => item.courseNodeId === cn.id) || feed.feed[feed.index]
    : feed.feed[feed.index];

  if (current && current.courseNodeId !== courseNodeId)
    yield put(yield call(updateState, { type: EntityTypes.COURSE_NODE, id: current.courseNodeId }));
}

function* prepareModule(module: IModuleAssign, courseProgressId: number, courseNodeId: number) {
  yield put(
    yield call(queueItem, {
      courseProgressId,
      courseNodeId,
      moduleId: module.id,
      sessionAttemptId: module.sessionAttemptId,
    }),
  );

  let loading = yield loadModule(module.id);

  while (loading) {
    const action = yield take(moduleRequest.fulfilled);
    loading = action.payload.id !== module.id;
  }

  const feed: IActionFeed = yield select(selectActionFeed);
  const actionId = yield select(state => state.state[EntityTypes.ACTION]);
  const current = actionId
    ? feed.feed.find(item => item.actionId === actionId) || feed.feed[feed.index]
    : feed.feed[feed.index];

  if (current && current.moduleId === module.id && current.sessionAttemptId === module.sessionAttemptId)
    yield call(startModule, module.sessionAttemptId, module.id, current.actionId);
  // else if (module.type === ModuleType.ASSESSMENT_RANDOM && module.order) {
  //   yield call(startModule, module.sessionAttemptId, module.id, module.order[0]);
  //}
}

function* playerResponseSaga({ payload, meta }) {
  yield handlePlayerResponse(payload, meta);
}

function* watchResponses() {
  yield takeEvery(
    [actionRequest.fulfilled, verificationRequest.fulfilled, dialogueRequest.fulfilled, completeRequest.fulfilled],
    playerResponseSaga,
  );
}

export function* responseSaga() {
  yield all([fork(watchResponses)]);
}
